import React, { useState, useEffect } from "react";
import { Search, Plus, Eye, Edit, Trash2, X } from "lucide-react";
import {
  useGetCategoryQuery,
  usePostCategoryMutation,
  useUpdateCategoryMutation,
  useSoftDeleteCategoryMutation,
} from "../../slices/assetApiSlice";
import { toast } from "sonner";
import DataTablePagination from "../../components/common/DataTablePagination";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import { Label } from "../../components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const Category = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [modalData, setModalData] = useState(null);
  const [editModalData, setEditModalData] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newCategory, setNewCategory] = useState({
    name: "",
    depreciatedValue: "",
  });

  // State for Alert Dialogs
  const [deleteId, setDeleteId] = useState(null);
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [updateConfirmOpen, setUpdateConfirmOpen] = useState(false);

  const { data: categories, error, isLoading, refetch } = useGetCategoryQuery();
  const [postCategory] = usePostCategoryMutation();
  const [updateCategory, { isLoading: isUpdating }] =
    useUpdateCategoryMutation();
  const [softDeleteCategory, { isLoading: isDeleting }] =
    useSoftDeleteCategoryMutation();

  const rowsPerPage = 10;

  const filteredCategories =
    categories?.filter((category) => category.deleted === false) || [];

  const filteredData = filteredCategories.filter((category) =>
    Object.values(category).some((value) =>
      value?.toString().toLowerCase().includes(searchTerm.toLowerCase()),
    ),
  );

  const totalPages = Math.ceil(filteredData.length / rowsPerPage);
  const displayedData = filteredData.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage,
  );

  const handleDeleteRow = (id) => {
    setDeleteId(id);
  };

  const confirmDelete = async () => {
    try {
      await softDeleteCategory(deleteId).unwrap();
      toast.success("Category deleted successfully!");
      refetch();
    } catch (err) {
      console.error("Error deleting category:", err);
      toast.error("Failed to delete category. Please try again.");
    } finally {
      setDeleteId(null);
    }
  };

  const handleEditRow = (item) => {
    setEditModalData(item);
  };

  const handleAddCategory = () => {
    setShowAddModal(true);
  };

  const handleSaveNewCategory = () => {
    if (!newCategory.name || !newCategory.depreciatedValue) {
      toast.error("Please fill in all required fields");
      return;
    }
    setSaveConfirmOpen(true);
  };

  const confirmSaveAdd = async () => {
    try {
      await postCategory(newCategory).unwrap();
      toast.success("Category created successfully!");
      setShowAddModal(false);
      setNewCategory({ name: "", depreciatedValue: "" });
      refetch();
    } catch (err) {
      console.error("Error creating category:", err);
      toast.error("Failed to create category. Please try again.");
    } finally {
      setSaveConfirmOpen(false);
    }
  };

  const handleView = (item) => {
    setModalData(item);
  };

  const handleUpdate = () => {
    setUpdateConfirmOpen(true);
  };

  const confirmUpdate = async () => {
    try {
      await updateCategory({
        id: editModalData.id,
        updatedCategory: {
          name: editModalData.name,
          depreciatedValue: editModalData.depreciatedValue,
        },
      }).unwrap();
      toast.success("Category updated successfully!");
      setEditModalData(null);
      refetch();
    } catch (err) {
      console.log(err);
      toast.error("Failed to update category. Please try again.");
    } finally {
      setUpdateConfirmOpen(false);
    }
  };

  return (
    <div className="flex flex-col h-screen bg-background">
      {/* Alert Dialogs */}
      <AlertDialog
        open={!!deleteId}
        onOpenChange={(open) => !open && setDeleteId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will delete the category. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete}>
              Yes, delete it!
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={saveConfirmOpen} onOpenChange={setSaveConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              You are about to add a new asset category!
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmSaveAdd}>
              Yes, add it!
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={updateConfirmOpen} onOpenChange={setUpdateConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              You are about to update this category!
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmUpdate}>
              Yes, update it!
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Header */}
      <div className="border-b bg-card px-6 py-4">
        <h1 className="text-2xl font-semibold">Asset Category</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Manage asset categories and their configurations
        </p>
      </div>

      {/* Controls */}
      <div className="border-b bg-card px-6 py-4">
        <div className="flex flex-col sm:flex-row items-center gap-4 justify-between">
          <div className="relative w-full sm:w-96">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <Input
              placeholder="Search categories..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          <Button
            onClick={handleAddCategory}
            className="w-full sm:w-auto"
            style={{
              backgroundColor: "rgb(48 88 69 / var(--tw-bg-opacity, 1))",
            }}
          >
            <Plus className="h-4 w-4 mr-2" />
            Create Category
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto p-6">
        <div className="rounded-md border bg-card">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="font-semibold">Sl. No.</TableHead>
                <TableHead className="font-semibold">Category</TableHead>
                <TableHead className="font-semibold">
                  Depreciated Value (%)
                </TableHead>
                <TableHead className="font-semibold text-center">
                  Actions
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {displayedData.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={4}
                    className="h-24 text-center text-muted-foreground"
                  >
                    No categories found.
                  </TableCell>
                </TableRow>
              ) : (
                displayedData
                  .sort((a, b) => (a.id < b.id ? 1 : -1))
                  .map((item, index) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        {(currentPage - 1) * rowsPerPage + index + 1}
                      </TableCell>
                      <TableCell className="font-medium">{item.name}</TableCell>
                      <TableCell>{item.depreciatedValue}%</TableCell>
                      <TableCell className="text-center">
                        <div className="flex justify-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleView(item)}
                            style={{
                              backgroundColor:
                                "rgb(48 88 69 / var(--tw-bg-opacity, 1))",
                              color: "white",
                            }}
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            View
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEditRow(item)}
                          >
                            <Edit className="h-4 w-4 mr-1" />
                            Edit
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Pagination */}
      <div className="border-t bg-card">
        <DataTablePagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={filteredData.length}
          itemsPerPage={rowsPerPage}
          onPageChange={setCurrentPage}
        />
      </div>

      {/* View Modal */}
      <Dialog open={!!modalData} onOpenChange={() => setModalData(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Asset Category Details</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Category Name</Label>
              <Input value={modalData?.name || ""} readOnly />
            </div>

            <div className="space-y-2">
              <Label>Depreciated Value (%)</Label>
              <Input value={modalData?.depreciatedValue || ""} readOnly />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="destructive"
              onClick={() => {
                handleDeleteRow(modalData.id);
                setModalData(null);
              }}
              disabled={isDeleting}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              {isDeleting ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Category Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create Asset Category</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Category Name</Label>
              <Input
                value={newCategory.name}
                onChange={(e) =>
                  setNewCategory({ ...newCategory, name: e.target.value })
                }
                placeholder="Enter category name"
              />
            </div>

            <div className="space-y-2">
              <Label>Depreciated Value (%)</Label>
              <Input
                type="number"
                value={newCategory.depreciatedValue}
                onChange={(e) =>
                  setNewCategory({
                    ...newCategory,
                    depreciatedValue: e.target.value,
                  })
                }
                placeholder="Enter depreciation percentage"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveNewCategory}
              disabled={isLoading}
              style={{
                backgroundColor: "rgb(48 88 69 / var(--tw-bg-opacity, 1))",
                color: "white",
              }}
            >
              {isLoading ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog
        open={!!editModalData}
        onOpenChange={() => setEditModalData(null)}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Asset Category</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Category Name</Label>
              <Input
                value={editModalData?.name || ""}
                onChange={(e) =>
                  setEditModalData({
                    ...editModalData,
                    name: e.target.value,
                  })
                }
              />
            </div>

            <div className="space-y-2">
              <Label>Depreciated Value (%)</Label>
              <Input
                type="number"
                value={editModalData?.depreciatedValue || ""}
                onChange={(e) =>
                  setEditModalData({
                    ...editModalData,
                    depreciatedValue: e.target.value,
                  })
                }
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditModalData(null)}>
              Cancel
            </Button>
            <Button
              onClick={handleUpdate}
              disabled={isUpdating}
              style={{
                backgroundColor: "rgb(48 88 69 / var(--tw-bg-opacity, 1))",
              }}
            >
              {isUpdating ? "Updating..." : "Update Category"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Category;
